<script>
    import { onMount } from "svelte";

    import { isMobile } from "../../stores";

    import { fly } from "svelte/transition";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        SearchSelect,
        LoadingModal,
        ResetFilterLink,
        DefaultDoctorFilter,
        DoctorMultiSearchSelect,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getAmountInCurrency,
        getFormatDateAndTime,
        getFormatDate,
        getText,
        goToURL,
        replaceURL,
        isObjectEmpty,
        showError,
        goToDownloadPage,
        getInvoiceStatus,
        decodeSearchParams,
        encodeSearchParams,
        syncDefaultDoctorFilter,
    } from "../../Utils/utilFunctions.svelte";

    import { invoiceStatus } from "../../Utils/utilEnums.svelte";

    import { user } from "../../stores";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    let isGettingGroupInvoices = false,
        returnGroupInvoicesArr = [],
        filters = { status: "1,2" },
        page = 0,
        pageSize = 25,
        count,
        checkboxString = "",
        selectedGroupInvoices = [],
        selectedGroupInvoicesIdArr = [],
        isDownloadingGroupInvoice = false,
        isRegeneratingGroupInvoice = false,
        showFilters = false;

    let selectedDoctors = [];

    const columns = [
        {
            key: "selected?",
            title: "",
            headerClass: "toggleSelectAllHeader text-center ml-3 rounded-tl-xl",
            renderValue: (v) =>
                `<input type="checkbox" class="checkbox checkbox-primary checkbox-xs ml-3 text-white cursor-pointer" name="selectCheckBox" id="selectCheckBoxId${v.id}">`,
            csvExportHidden: true,
        },
        {
            key: "id",
            title: "Invoice ID",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "invoiceNumber",
            title: "Invoice Number",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.invoiceNumber),
        },
        {
            key: "group",
            title: "Group",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getGroupName(v.billingDetails),
        },
        {
            key: "month",
            title: "Month",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) =>
                _getFormatDate(v.createdDate, false, true, true),
        },
        {
            key: "createdDate",
            title: "Create Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.createdDate, false, true),
        },
        {
            key: "dueDate",
            title: "Due Date",
            headerClass: "text-left px-3",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getFormatDate(v.dueDate, false, true),
        },
        {
            key: "subTotal",
            title: "Subtotal",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee),
        },
        {
            key: "gst",
            title: "GST",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getAmountInCurrency(v.totalFee / 10),
        },
        {
            key: "totalFee",
            title: "Total",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            value: (v) =>
                getAmountInCurrency(v.totalFee * (v.addGST ? 1.1 : 1)),
        },
        {
            key: "status",
            title: "Status",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _getStatus(v.status),
            csvContent: (v) => _getStatus(v.status, true),
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-center px-3 cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "invoiceNumber", "createdDate"];

    $: isUserEnableViewGroupInvoice = $user.role == 100 || $user.role == 20;

    $: _getCheckboxString(selectedGroupInvoicesIdArr);
    $: if (
        selectedGroupInvoices.length >= 0 &&
        returnGroupInvoicesArr.length > 0
    ) {
        selectedGroupInvoicesIdArr = selectedGroupInvoices.map(
            (invoice) => invoice.id,
        );
        _updateHeaderIcon();
    }

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        loadGroupInvoices(page * pageSize, pageSize, _getSearchParams());

        if (params.query) updateCheckboxByQuery(params.query);
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("groupId="))
                    filters.groupId = arr[i].split("=")[1].toString();

                if (arr[i].includes("status="))
                    filters.status = isNaN(arr[i].split("=")[1])
                        ? arr[i].split("=")[1]
                        : Number(arr[i].split("=")[1]);

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("createddatefrom="))
                    filters.createddatefrom = arr[i].split("=")[1];

                if (arr[i].includes("createddateto="))
                    filters.createddateto = arr[i].split("=")[1];
            }
            updateURL();
        }
    };

    const updateCheckboxByQuery = (query) => {
        const arr = query.split("&");
        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("check=")) {
                    const checkArr = arr[i].split("=")[1].split(",");
                    checkArr.map((ca) => {
                        if (
                            !selectedGroupInvoices.some(
                                (p) => p.id == Number(ca),
                            )
                        ) {
                            let newInvoiceItem = { id: Number(ca) };
                            selectedGroupInvoices = [
                                ...selectedGroupInvoices,
                                newInvoiceItem,
                            ];
                            const checkbox = document.getElementById(
                                `selectCheckBoxId${ca}`,
                            );
                            if (checkbox) checkbox.checked = true;
                        }
                    });
                }
            }
        }
    };

    const loadGroupInvoices = async (start, limit, params) => {
        isGettingGroupInvoices = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getExternalGroupInvoices(start, limit, params)
            .then((res) => _handleGroupInvoices(res))
            .then(() => _updateHeaderSortIcon());

        isGettingGroupInvoices = false;
    };

    const handleFilterSelect = () => {
        page = 0;
        selectedGroupInvoices = [];

        updateURL();

        loadGroupInvoices(0, pageSize, _getSearchParams());
    };

    const generateSelectAll = () => {
        const selectAllHeader = document.getElementsByClassName(
            "toggleSelectAllHeader",
        )[0];
        const tag = document.createElement("input");
        tag.type = "checkbox";
        tag.className =
            "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white ml-3 mt-2";
        tag.id = "toggleSelectAll";
        tag.onclick = () => {
            toggleSelectAll();
        };
        if (selectAllHeader) selectAllHeader.appendChild(tag);
    };

    const toggleSelectAll = () => {
        const selectAllCheckBox = document.getElementById("toggleSelectAll");
        if (selectAllCheckBox.className.includes("selectedAll")) {
            selectedGroupInvoices = [];
            _updateCheckBox();
        } else {
            returnGroupInvoicesArr.forEach((invoice) => {
                if (!selectedGroupInvoicesIdArr.includes(invoice.id)) {
                    _addSelectedInvoice(invoice);
                }
            });
        }
    };

    const updateURL = () => {
        if (checkboxString)
            replaceURL(
                `/GroupInvoices/page=${page}&size=${pageSize}&check=${checkboxString}${_getParamsString()}`,
            );
        else
            replaceURL(
                `/GroupInvoices/page=${page}&size=${pageSize}${_getParamsString()}`,
            );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadGroupInvoices(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );

        _updateCheckBox();
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadGroupInvoices(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "selected?") _addSelectedInvoice(rowData);
        else if (key == "actions") {
            if (className.includes("downloadIcon"))
                downloadGroupInvoice(rowData.id);
            // else if (className.includes("reGenerateIcon"))
            //     regenerateGroupInvoice(rowData);
        } else if (rowData.id && isUserEnableViewGroupInvoice)
            goToURL(`/GroupInvoices/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (key == "selected?") _updateCheckBox();
        else if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadGroupInvoices(0, pageSize, _getSearchParams());
        }
    };

    const downloadGroupInvoice = (id, isRegenerate) => {
        goToDownloadPage(14, id, isRegenerate);
    };

    // HELPER FUNCTIONS
    const _handleGroupInvoices = (res) => {
        if (res.error) showError(res.error);
        else {
            returnGroupInvoicesArr = [...res.results];
            count = res.total;
        }

        setTimeout(() => {
            generateSelectAll();
            _updateCheckBox();
        }, 1);
    };

    const _updateCheckBox = () => {
        setTimeout(() => {
            returnGroupInvoicesArr.forEach((invoice) => {
                const checkbox = document.getElementById(
                    `selectCheckBoxId${invoice.id}`,
                );
                if (checkbox) {
                    if (selectedGroupInvoicesIdArr.includes(invoice.id)) {
                        document.getElementById(
                            `selectCheckBoxId${invoice.id}`,
                        ).checked = true;
                    } else {
                        document.getElementById(
                            `selectCheckBoxId${invoice.id}`,
                        ).checked = false;
                    }
                }
            });
            _updateHeaderIcon();
        }, 1);
    };

    const _addSelectedInvoice = (data) => {
        if (selectedGroupInvoices.some((invoice) => invoice.id === data.id)) {
            selectedGroupInvoices = selectedGroupInvoices.filter(
                (invoice) => invoice.id !== data.id,
            );
        } else {
            selectedGroupInvoices = [...selectedGroupInvoices, data];
            const checkbox = document.getElementById(
                `selectCheckBoxId${data.id}`,
            );
            if (checkbox) checkbox.checked = true;
        }
    };

    const _getCheckboxString = (arr) => {
        if (arr.length) checkboxString = arr.toString();
        else checkboxString = "";

        updateURL();
    };

    const _updateHeaderIcon = () => {
        const headerIcon = document.getElementById("toggleSelectAll");
        if (!headerIcon) return;
        if (selectedGroupInvoices.length === 0) {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedNone";
            headerIcon.checked = false;
            headerIcon.style = "";
        } else if (
            returnGroupInvoicesArr.every((invoice) =>
                selectedGroupInvoicesIdArr.includes(invoice.id),
            )
        ) {
            headerIcon.style.cssText = "";
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 selectedAll";
            headerIcon.checked = true;
        } else {
            headerIcon.className =
                "toggleSelectAll text-center hover:cursor-pointer checkbox checkbox-primary checkbox-xs bg-white mt-2 ml-3 appearance-none selectedPartial";
            headerIcon.checked = false;
            headerIcon.style.cssText =
                "border: solid #270053; border-width: 4px; padding: 4px; border-radius: 5px; background-color: white; cursor: pointer; margin: 0.5rem 0 0 0.75rem";
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Invoice ID") ||
                        (params.sort == "invoiceNumber" &&
                            row.childNodes[0].data == "Invoice Number") ||
                        (params.sort == "createdDate" &&
                            row.childNodes[0].data == "Create Date")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "Invoice ID") ||
                        (params.sort == "invoiceNumberDesc" &&
                            row.childNodes[0].data == "Invoice Number") ||
                        (params.sort == "createdDateDesc" &&
                            row.childNodes[0].data == "Create Date")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (filters.groupId) params["filter[groupId]"] = filters.groupId;

        if (filters.status) params["filter[status]"] = filters.status;

        if (filters.createddatefrom)
            params["filter[createddatefrom]"] = filters.createddatefrom;

        if (filters.createddateto)
            params["filter[createddateto]"] = filters.createddateto;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            const filterKeys = [
                "groupId",
                "status",
                "createddatefrom",
                "createddateto",
                "doctorId",
            ];

            filterKeys.forEach((key) => {
                if (params[`filter[${key}]`])
                    str += `&${key}=` + params[`filter[${key}]`];
            });
        }

        return encodeSearchParams(str);
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Invoice PDF">
                        <i class="downloadIcon fas fa-download"></i>
                    </div>
                </div>`;

        // <div class="reGenerateIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Re-generate Invoice PDF">
        //     <i class="reGenerateIcon fas fa-arrows-rotate"></i>
        // </div>
    };

    const _getGroupName = (billingDetails) => {
        if (isObjectEmpty(billingDetails)) return "";

        return getText(billingDetails.name);
    };

    const _getFormatDate = (date, isExpired, isDateOnly, hideDate = false) => {
        if (isExpired) return "Expired";

        if (isDateOnly)
            return hideDate
                ? getFormatDate(date).substring(3)
                : getFormatDate(date);

        return getFormatDateAndTime(date);
    };
    const _getStatus = (status, isCsvContent = false) => {
        let statusText, statusColor;

        statusText = getInvoiceStatus(status);

        if (isCsvContent) return statusText;

        switch (status) {
            case 1:
            case 2:
                statusColor = "#2DBD6F";
                break;
            case 9:
                statusColor = "#E05757";
                break;
            default:
                statusColor = "";
        }

        return `
                <div class="flex justify-start items-center">
                    <div style="color: ${statusColor};">${statusText}</div>
                </div>
                `;
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => fly(node, args);
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click={toggleFilters}
        >
            Filters
            <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>

        {#if showFilters}
            <div
                class="flex flex-0 items-end {$isMobile
                    ? 'flex-col-reverse gap-y-4'
                    : 'justify-between'}"
                in:filterTransition={{ duration: 200, y: -50 }}
            >
                <div class="flex gap-4 flex-wrap items-end">
                    <SearchInput
                        bind:value={filters.groupId}
                        title="Group ID"
                        inputFunc={handleFilterSelect}
                    />

                    <SearchSelect
                        bind:value={filters.status}
                        title="Status"
                        onChangeFunc={handleFilterSelect}
                    >
                        <option value="1,2" selected>
                            All except cancelled
                        </option>
                        {#each invoiceStatus as { id, type }}
                            <option value={id} selected={id == filters.status}>
                                {type}
                            </option>
                        {/each}
                    </SearchSelect>

                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />

                    {#if !$user.settings?.disableDateRangeInput}
                        <SearchDateRangeInput
                            bind:startDate={filters.createddatefrom}
                            bind:endDate={filters.createddateto}
                            title="Invoice Date Range"
                            inputClass="borderB bg-[#EEE]"
                            showClearButton
                            disabled={isGettingGroupInvoices}
                            showScrollYearOptions
                        />
                    {:else}
                        <SearchInput
                            bind:value={filters.createddatefrom}
                            title="Fortnight ending from"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingGroupInvoices}
                            maxDate={filters.createddateto ?? undefined}
                            showScrollYearOptions
                        />

                        <SearchInput
                            bind:value={filters.createddateto}
                            title="Fortnight ending to"
                            type="date"
                            inputClass="borderB"
                            inputFunc={handleFilterSelect}
                            isDisable={isGettingGroupInvoices}
                            minDate={filters.createddatefrom ?? undefined}
                            showScrollYearOptions
                        />
                    {/if}
                    <ResetFilterLink
                        bind:filter={filters}
                        onResetFilter={() => {
                            handleFilterSelect();
                            clearFilterDoctor();
                        }}
                    />
                </div>
            </div>
        {/if}
    {:else}
        <div
            class="flex flex-0 items-end justify-between"
            in:filterTransition={{ duration: 200, y: -50 }}
        >
            <div class="flex gap-4 flex-wrap items-end">
                <SearchInput
                    bind:value={filters.groupId}
                    title="Group ID"
                    inputFunc={handleFilterSelect}
                />
                <SearchSelect
                    bind:value={filters.status}
                    title="Status"
                    onChangeFunc={handleFilterSelect}
                >
                    <option value="1,2" selected> All except cancelled </option>
                    {#each invoiceStatus as { id, type }}
                        <option value={id} selected={id == filters.status}>
                            {type}
                        </option>
                    {/each}
                </SearchSelect>

                <DefaultDoctorFilter
                    bind:filter={filters}
                    filterPropertyName={"doctorId"}
                    onChangeFunc={() => {
                        selectedDoctors = filters["doctorId"].split(",");
                        handleFilterSelect();
                    }}
                />

                <DoctorMultiSearchSelect
                    bind:searchResult={selectedDoctors}
                    onChangeFunc={handleSelectDoctor}
                    inputClass="w-32"
                    zIndex="3"
                />

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.createddatefrom}
                        bind:endDate={filters.createddateto}
                        title="Invoice Date Range"
                        inputClass="borderB bg-[#EEE]"
                        showClearButton
                        disabled={isGettingGroupInvoices}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.createddatefrom}
                        title="Fortnight ending from"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingGroupInvoices}
                        maxDate={filters.createddateto ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.createddateto}
                        title="Fortnight ending to"
                        type="date"
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingGroupInvoices}
                        minDate={filters.createddatefrom ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={() => {
                        handleFilterSelect();
                        clearFilterDoctor();
                    }}
                />
            </div>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingGroupInvoices}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnGroupInvoicesArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        showSelected
        selectedCount={selectedGroupInvoices.length}
        tableData={returnGroupInvoicesArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnGroupInvoicesArr,
            fileName: "Group Invoices",
            allDataApi: async () =>
                await apiMethods.getMethod.getExternalGroupInvoices(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if isDownloadingGroupInvoice}
    <LoadingModal titleText="Downloading Invoice PDF..." />
{/if}

{#if isRegeneratingGroupInvoice}
    <LoadingModal titleText="Regenerating Invoice PDF..." />
{/if}
